/**
 * EmptyState — empty data placeholder primitive.
 *
 * Renders the canonical "this surface has no data yet" message:
 * an icon, a headline, an optional body, and an optional CTA. The
 * shell uses already-tokenized chrome (`bg-card`, `rounded-card`,
 * `shadow-subtle`, `border border-border`) so it integrates with
 * the rest of the design system without needing per-surface styling.
 *
 * Per-surface empty-state CONTENT (the icon, headline copy, body
 * copy, CTA destination) is intentionally NOT included here. Each
 * consuming surface authors its own per-screen empty-state with
 * context-aware copy. Examples:
 *
 *   <EmptyState
 *     icon={<Trophy className="h-10 w-10" />}
 *     headline="No scorecards yet"
 *     body="Scorecards will appear here when judges submit their evaluations."
 *     cta={
 *       <Link to="/admin/calendar" className="... min-h-[var(--touch-target-min)] ...">
 *         View Schedule →
 *       </Link>
 *     }
 *   />
 *
 * Spec: meridian-redesign — task (m.1).x (EmptyState primitive)
 * Requirements: 14.1 (a11y — landmark region for empty content)
 *
 * Per-surface empty-state authoring is deferred to future spec work
 * (recorded in design.md under "system extensions"). PR (m.3) wires
 * 1-2 exemplar instances as templates.
 */

import type { JSX, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface EmptyStateProps {
  /** Lucide icon (or any ReactNode). Rendered above the headline. */
  icon?: ReactNode;
  /** Primary heading. Required. */
  headline: string;
  /** Secondary description. Optional. */
  body?: string;
  /** Action affordance (e.g. a button or link). Optional. */
  cta?: ReactNode;
  /** Optional extra classes — merged via `cn()`. */
  className?: string;
  /** Padding variant — small variants stack inside cards, large
   *  variants stand alone as full-page empty states. */
  padding?: 'comfortable' | 'roomy';
}

export function EmptyState({
  icon,
  headline,
  body,
  cta,
  className,
  padding = 'comfortable',
}: EmptyStateProps): JSX.Element {
  return (
    <div
      role="status"
      data-testid="empty-state"
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-center',
        'bg-card border border-border rounded-card shadow-subtle',
        padding === 'roomy' ? 'p-card-roomy' : 'p-card-comfortable',
        className,
      )}
    >
      {icon ? (
        <div className="text-muted-foreground" aria-hidden="true">
          {icon}
        </div>
      ) : null}
      <h3 className="font-display text-display-sm text-foreground ">
        {headline}
      </h3>
      {body ? (
        <p className="font-sans text-body text-muted-foreground max-w-prose">
          {body}
        </p>
      ) : null}
      {cta ? <div className="mt-2">{cta}</div> : null}
    </div>
  );
}

export default EmptyState;
